import { MessageOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Collapse, Empty, Input, Segmented, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { useMemo, useState } from "react";

import { clientAdvisorChatType, formatSessionDate } from "@fitness-app/client-shared/features/client-advisor";
import { type AgentChatMessage, type AgentChatSession } from "@fitness-app/data-models";

import { getMessageText, mapStoredMessage } from "~/modules/Sales/SalesAgent/messageUtils";
import { supabase } from "~/store/initializeStore";
import { StartAdvisorConversationModal } from "./StartAdvisorConversationModal";
import { useTranslation as useInterfaceTranslation } from "react-i18next";

// TRE-1613 — read-only preview of client advisor conversations (compliance, F4.1) plus
// the agent-initiated conversation entry point.
// RLS (agent_chat_can_read_owner) lets the TRAINER claim and active shared trainers
// read every client_advisor session; clients only ever see their own.

const ADVISOR_SESSIONS_QUERY_KEY = "clientAdvisorSessions";

type SessionOwner = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
};

type OwnerTrainee = {
  id: string;
  userId: string | null;
  email: string;
};

type ClientGroup = {
  key: string;
  label: string;
  sessions: AgentChatSession[];
  traineeId: string | null;
  lastMessageAt: string | null;
};

const conversationsCountLabel = (count: number) => {
  if (count === 1) return "1 rozmowa";
  const rest = count % 10;
  const teens = count % 100;
  const usesPluralFew = rest >= 2 && rest <= 4 && (teens < 12 || teens > 14);
  return `${count} ${usesPluralFew ? "rozmowy" : "rozmów"}`;
};

const roleLabels: Record<string, { label: string; color: string }> = {
  user: { label: "Klientka", color: "blue" },
  assistant: { label: "Opiekunka", color: "gold" },
};

const AdvisorMessage = ({ chatMessage }: { chatMessage: AgentChatMessage }): React.ReactElement => {
  const { t: tUi } = useInterfaceTranslation();
  const role = roleLabels[chatMessage.role] ?? { label: chatMessage.role, color: "default" };
  const text = getMessageText(mapStoredMessage(chatMessage));

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
      <div className="mb-1 flex items-center justify-between gap-3">
        <Tag color={role.color}>{role.label}</Tag>
        <Typography.Text type="secondary" className="text-xs">
          {formatSessionDate(tUi, chatMessage.createdAt)}
        </Typography.Text>
      </div>
      {text ? (
        <Typography.Paragraph className="!mb-0 whitespace-pre-wrap text-sm">{text}</Typography.Paragraph>
      ) : (
        <Typography.Text type="secondary" italic className="text-xs">
          (wiadomość bez treści tekstowej — np. wywołanie narzędzia)
        </Typography.Text>
      )}
    </div>
  );
};

const isAdvisorInitiated = (session: AgentChatSession) =>
  (session.metadata as { initiatedBy?: unknown } | null)?.initiatedBy === "advisor";

export const AdvisorConversationsTab = (): React.ReactElement => {
  const { t: tUi } = useInterfaceTranslation();
  const queryClient = useQueryClient();
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [groupByClient, setGroupByClient] = useState(false);
  const [search, setSearch] = useState("");
  const [startModalTraineeId, setStartModalTraineeId] = useState<string | null>(null);
  const [startModalOpen, setStartModalOpen] = useState(false);

  const sessionsQuery = useQuery({
    queryKey: [ADVISOR_SESSIONS_QUERY_KEY],
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_chat_session")
        .select("*")
        .eq("agentType", clientAdvisorChatType)
        .order("lastMessageAt", { ascending: false })
        .limit(200);

      if (error) throw new Error(error.message);
      return (data ?? []) as AgentChatSession[];
    },
  });

  const sessions = useMemo(() => sessionsQuery.data ?? [], [sessionsQuery.data]);
  const ownerIds = useMemo(
    () => [...new Set(sessions.map((session) => session.ownerId).filter((id): id is string => Boolean(id)))],
    [sessions],
  );

  const ownersQuery = useQuery({
    queryKey: ["clientAdvisorSessionOwners", ownerIds],
    enabled: ownerIds.length > 0,
    retry: false,
    queryFn: async () => {
      // Best effort: if RLS blocks user_profile reads, fall back to raw ownerId in the list.
      const [{ data: profiles }, { data: trainees }] = await Promise.all([
        supabase.from("user_profile").select("id, email, firstName, lastName").in("id", ownerIds),
        supabase.from("trainee").select('id, "userId", email').in("userId", ownerIds),
      ]);

      return {
        owners: (profiles ?? []) as SessionOwner[],
        trainees: (trainees ?? []) as OwnerTrainee[],
      };
    },
  });

  const ownersById = useMemo(
    () => new Map((ownersQuery.data?.owners ?? []).map((owner) => [owner.id, owner])),
    [ownersQuery.data],
  );
  const traineeByUserId = useMemo(
    () =>
      new Map(
        (ownersQuery.data?.trainees ?? [])
          .filter((trainee): trainee is OwnerTrainee & { userId: string } => Boolean(trainee.userId))
          .map((trainee) => [trainee.userId, trainee]),
      ),
    [ownersQuery.data],
  );

  const getOwnerLabel = (session: AgentChatSession) => {
    if (!session.ownerId) return "Nieznana klientka";
    const trainee = traineeByUserId.get(session.ownerId);
    if (trainee?.email) return trainee.email;

    const owner = ownersById.get(session.ownerId);
    if (!owner) return session.ownerId;
    const fullName = [owner.firstName, owner.lastName].filter(Boolean).join(" ");
    return owner.email || fullName || session.ownerId;
  };

  // Search over the loaded sessions (e-mail first, plus name and conversation title,
  // so typing a fragment of any of them narrows both views).
  const filteredSessions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sessions;

    return sessions.filter((session) => {
      const owner = session.ownerId ? ownersById.get(session.ownerId) : null;
      const haystack = [getOwnerLabel(session), owner?.email, owner?.firstName, owner?.lastName, session.title]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [search, sessions, ownersById, traineeByUserId]);

  // Grouped view: one entry per client (by e-mail), sessions newest-first inside.
  const clientGroups = useMemo<ClientGroup[]>(() => {
    const groups = new Map<string, ClientGroup>();

    for (const session of filteredSessions) {
      const key = session.ownerId ?? "unknown";
      const existing = groups.get(key);

      if (existing) {
        existing.sessions.push(session);
        continue;
      }

      groups.set(key, {
        key,
        label: getOwnerLabel(session),
        sessions: [session],
        traineeId: session.ownerId ? (traineeByUserId.get(session.ownerId)?.id ?? null) : null,
        lastMessageAt: session.lastMessageAt,
      });
    }

    return [...groups.values()].sort((a, b) => (b.lastMessageAt ?? "").localeCompare(a.lastMessageAt ?? ""));
  }, [filteredSessions, ownersById, traineeByUserId]);

  const messagesQuery = useQuery({
    queryKey: ["clientAdvisorSessionMessages", activeSessionId],
    enabled: Boolean(activeSessionId),
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_chat_message")
        .select("*")
        .eq("sessionId", activeSessionId!)
        .order("createdAt", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as AgentChatMessage[];
    },
  });

  const activeSession = sessions.find((session) => session.id === activeSessionId) ?? null;

  const openStartModal = (traineeId: string | null) => {
    setStartModalTraineeId(traineeId);
    setStartModalOpen(true);
  };

  const renderSessionButton = (session: AgentChatSession, withOwner: boolean) => (
    <button
      key={session.id}
      type="button"
      onClick={() => setActiveSessionId(session.id)}
      className={[
        "block w-full rounded-md border px-3 py-2 text-left transition-colors",
        session.id === activeSessionId
          ? "border-[#a28f70] bg-[#f8f4ee]"
          : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50",
      ].join(" ")}
    >
      {withOwner ? <div className="truncate text-sm font-semibold text-gray-900">{getOwnerLabel(session)}</div> : null}
      <div className="mt-0.5 flex items-center gap-2">
        <div className="min-w-0 flex-1 truncate text-xs text-gray-600">{session.title}</div>
        {isAdvisorInitiated(session) ? (
          <Tooltip title="Rozmowę rozpoczął agent">
            <Tag color="gold" className="!mr-0 shrink-0">
              agent
            </Tag>
          </Tooltip>
        ) : null}
      </div>
      <div className="mt-1 text-xs text-gray-500">{formatSessionDate(tUi, session.lastMessageAt)}</div>
    </button>
  );

  return (
    <>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[380px_minmax(0,1fr)]">
        <Card
          title="Rozmowy klientek"
          loading={sessionsQuery.isLoading}
          styles={{ body: { maxHeight: "calc(100vh - 360px)", overflowY: "auto" } }}
          extra={
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openStartModal(null)}>
              Nowa rozmowa
            </Button>
          }
        >
          <Input
            allowClear
            className="mb-3"
            placeholder="Szukaj po e-mailu klientki"
            prefix={<SearchOutlined className="text-gray-400" />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <Segmented
            block
            className="mb-3"
            value={groupByClient ? "grouped" : "flat"}
            onChange={(value) => setGroupByClient(value === "grouped")}
            options={[
              { label: "Lista", value: "flat" },
              { label: "Wg klientki", value: "grouped" },
            ]}
          />

          {sessionsQuery.isError ? (
            <Alert
              type="warning"
              showIcon
              message="Nie udało się pobrać rozmów"
              description={(sessionsQuery.error as Error).message}
              className="mb-3"
            />
          ) : null}

          {!filteredSessions.length && !sessionsQuery.isError ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={search.trim() ? `Brak rozmów dla „${search.trim()}"` : "Brak rozmów z Opiekunką"}
            />
          ) : groupByClient ? (
            <Collapse
              accordion
              size="small"
              items={clientGroups.map((group) => ({
                key: group.key,
                label: (
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900">{group.label}</div>
                    <div className="text-xs text-gray-500">
                      {conversationsCountLabel(group.sessions.length)} · {formatSessionDate(tUi, group.lastMessageAt)}
                    </div>
                  </div>
                ),
                extra: group.traineeId ? (
                  <Tooltip title="Nowa rozmowa z tą klientką">
                    <Button
                      size="small"
                      type="text"
                      icon={<MessageOutlined />}
                      onClick={(event) => {
                        event.stopPropagation();
                        openStartModal(group.traineeId);
                      }}
                    />
                  </Tooltip>
                ) : null,
                children: (
                  <div className="space-y-2">
                    {group.sessions.map((session) => renderSessionButton(session, false))}
                  </div>
                ),
              }))}
            />
          ) : (
            <div className="space-y-2">{filteredSessions.map((session) => renderSessionButton(session, true))}</div>
          )}
        </Card>

        <Card
          title={activeSession ? `${getOwnerLabel(activeSession)} — ${activeSession.title}` : "Podgląd rozmowy"}
          styles={{ body: { maxHeight: "calc(100vh - 360px)", overflowY: "auto" } }}
        >
          {!activeSessionId ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Wybierz rozmowę z listy" />
          ) : messagesQuery.isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <Spin />
            </div>
          ) : messagesQuery.isError ? (
            <Alert
              type="warning"
              showIcon
              message="Nie udało się pobrać wiadomości"
              description={(messagesQuery.error as Error).message}
            />
          ) : (messagesQuery.data ?? []).length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak wiadomości w tej rozmowie" />
          ) : (
            <Space direction="vertical" size="small" className="w-full">
              {(messagesQuery.data ?? []).map((chatMessage) => (
                <AdvisorMessage key={chatMessage.id} chatMessage={chatMessage} />
              ))}
            </Space>
          )}
        </Card>
      </div>

      <StartAdvisorConversationModal
        defaultTraineeId={startModalTraineeId}
        open={startModalOpen}
        onClose={() => setStartModalOpen(false)}
        onStarted={() => {
          // Generation runs in trigger.dev — refetch shortly after so the new session shows up.
          setTimeout(() => void queryClient.invalidateQueries({ queryKey: [ADVISOR_SESSIONS_QUERY_KEY] }), 8000);
        }}
      />
    </>
  );
};
